import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl, createApiHeaders } from '../../../config/api';

/**
 * Custom hook for loading template versions
 * Fetches all versions for a template including their status
 *
 * @param {string} templateId - Template ID
 * @returns {Object} Versions state and loading function
 */
export const useTemplateVersions = templateId => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load template versions from advanced endpoint
   * Fetches all pages to get all versions
   */
  const loadVersions = useCallback(async () => {
    if (!templateId) {
      setVersions([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = createApiHeaders();
      const allVersions = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 100; // Use reasonable page size

      // Fetch all pages until we have all versions
      while (hasMore) {
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: pageSize.toString(),
        });

        // Try advanced endpoint first, fallback to basic endpoint
        let response = await fetch(
          `${getApiBaseUrl()}/api/v1/templates/advanced/${templateId}/versions?${params.toString()}`,
          { headers }
        );

        // If advanced endpoint doesn't exist, fallback to basic
        if (!response.ok && response.status === 404) {
          response = await fetch(
            `${getApiBaseUrl()}/api/v1/templates/${templateId}/versions?${params.toString()}`,
            { headers }
          );
        }

        if (!response.ok) {
          throw new Error(`Failed to load versions: ${response.status}`);
        }

        const data = await response.json();
        // Handle both response formats: {versions: [...]} or {templates: [...]}
        const versionsList = data.versions || data.templates || [];
        allVersions.push(...versionsList);

        // Check if there are more pages
        const total = data.total || 0;
        const currentPage = data.page || page;
        const totalPages = Math.ceil(total / pageSize);
        hasMore = currentPage < totalPages && versionsList.length === pageSize;

        page++;
      }

      setVersions(allVersions);
    } catch (err) {
      setError(err.message);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  // Load versions when templateId changes
  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  return {
    versions,
    loading,
    error,
    loadVersions,
  };
};
